import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ShieldCheck, Loader2, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import type { VerifyResult } from '@mdu-invoicing/shared';
import { apiFetch, ApiError } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

// Mirror of the API's LogEntryDTO (the DTO lives in packages/api and is not
// importable here — only the shared crypto types are). Keep the fields rendered.
interface LogEntry {
  seq: string;
  invoicingMonth: string;
  stream: string;
  mduId: string;
  ssvContractor: string;
  designQuadrant: string;
  invoicingEligibilityCheckBy: string;
  invoicingEligibilityCheckDate: string;
  rowOrigin: string;
  totalHash: string;
  createdAt: string;
  /** Full point-in-time record (top-level §5.6 fields + the VIEW_ELIGIBLES snapshot). */
  payload?: Record<string, unknown>;
}

interface LogResponse {
  entries: LogEntry[];
  count: number;
}

/** ISO timestamp → 'DD/MM/YYYY' (date part only). */
function frDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/** Display a snapshot value; null/undefined/'' → '—'. */
function val(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/** The full point-in-time record (spec table C) shown when a log row is expanded. */
function LogDetail({ payload }: { payload?: Record<string, unknown> }) {
  if (!payload) return <span className="text-sm text-slate-400">Aucun détail enregistré pour cette entrée.</span>;
  const snap = (payload.snapshot ?? {}) as Record<string, unknown>;
  const amount = snap.amountCents;
  const fields: { label: string; value: string }[] = [
    { label: 'Area Name', value: val(snap.areaName ?? snap.areaId) },
    { label: "Type d'adresse", value: val(snap.buildingGroupAddressType) },
    { label: 'Adresse', value: val(snap.buildingGroupMainAddress) },
    { label: 'MDU in DMP', value: val(snap.mduInDmp) },
    { label: 'Design Quadrant', value: val(snap.designQuadrant) },
    { label: 'SSV Status', value: val(snap.ssvStatus) },
    { label: 'SSV Done', value: val(snap.ssvDone) },
    { label: 'SSV Date', value: val(snap.ssvDate) },
    { label: 'Statut validation SSV (PBI)', value: val(snap.ssvValidationStatus) },
    { label: 'TSA Status', value: val(snap.tsaStatus) },
    { label: 'TSA Done', value: val(snap.tsaDone) },
    { label: 'TSA Date', value: val(snap.tsaDate) },
    { label: 'Statut validation TSA (PBI)', value: val(snap.tsaValidationStatus) },
    { label: 'Éligibilité (partenaire)', value: val(snap.providerEligibility) },
    { label: 'Décision Unifiber', value: val(snap.proposalDecision) },
    { label: 'Commentaire partenaire', value: val(snap.eligibilityComment) },
    { label: 'Commentaire Unifiber', value: val(snap.proposalComment) },
    { label: 'Type de service', value: val(snap.serviceType) },
    { label: 'Montant', value: typeof amount === 'number' ? `€${(amount / 100).toFixed(2)}` : '—' },
    { label: 'Eligible for invoicing', value: payload.eligibleForInvoicing ? 'OK' : '—' },
    { label: 'Invoicing eligibility check', value: val(payload.invoicingEligibilityCheck) },
    { label: 'Commentaire éligibilité', value: val(payload.invoicingEligibilityComment) },
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs md:grid-cols-3 lg:grid-cols-4">
      {fields.map((f) => (
        <div key={f.label} className="flex flex-col">
          <dt className="font-semibold uppercase tracking-wide text-slate-400">{f.label}</dt>
          <dd className="text-slate-700">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Log() {
  const [searchParams] = useSearchParams();
  const initialMduId = searchParams.get('mduId') ?? '';

  const [mduId, setMduId] = useState(initialMduId);
  const [month, setMonth] = useState('');

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Which rows have their full-record detail (spec table C) expanded.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (seq: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });

  // Guard async setState against an unmount mid-request (consistent with the
  // other screens' `cancelled` pattern).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async (filterMduId: string, filterMonth: string) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (filterMduId.trim()) params.set('mduId', filterMduId.trim());
    if (filterMonth.trim()) params.set('invoicingMonth', filterMonth.trim());
    const qs = params.toString();
    try {
      const data = await apiFetch<LogResponse>(`/invoicing-log${qs ? `?${qs}` : ''}`);
      if (mounted.current) setEntries(data.entries);
    } catch (e) {
      if (mounted.current) {
        setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
        setEntries([]);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  // Initial load (honours a ?mduId deep-link from the "déjà facturé" flag).
  useEffect(() => {
    void load(initialMduId, '');
  }, [load, initialMduId]);

  async function handleVerify() {
    setVerifying(true);
    setVerifyError(null);
    setVerifyResult(null);
    try {
      const result = await apiFetch<VerifyResult>('/invoicing-log/verify-integrity');
      if (mounted.current) setVerifyResult(result);
    } catch (e) {
      if (mounted.current) setVerifyError(e instanceof ApiError ? e.message : 'Échec de la vérification');
    } finally {
      if (mounted.current) setVerifying(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* OQ-10 caveat */}
      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          <span className="font-semibold">Mécanisme d'intégrité — proposition en attente de validation FA (OQ-10).</span>{' '}
          Le journal est conçu inaltérable (chaîne de hachage + signature), mais le mécanisme n'est pas encore
          ratifié : à ne pas considérer comme garantie d'audit définitive avant accord.
        </p>
      </div>

      {/* Filters + verify */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="log-mdu" className="mb-1 block text-xs font-medium text-slate-500">
              MDU_ID
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="log-mdu"
                value={mduId}
                onChange={(e) => setMduId(e.target.value)}
                placeholder="MDU-0001"
                className="w-44 rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
              />
            </div>
          </div>
          <div>
            <label htmlFor="log-month" className="mb-1 block text-xs font-medium text-slate-500">
              Mois (AAAA-MM)
            </label>
            <input
              id="log-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="2026-06"
              className="w-32 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
            />
          </div>
          <button
            type="button"
            onClick={() => void load(mduId, month)}
            className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            Filtrer
          </button>
        </div>

        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
        >
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Vérifier l'intégrité
        </button>
      </div>

      {/* Verify result */}
      {verifyError && (
        <Card className="p-4 text-sm text-rose-700">Vérification impossible : {verifyError}.</Card>
      )}
      {verifyResult &&
        (verifyResult.valid ? (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            Chaîne intègre — {verifyResult.rowCount} entrée(s) vérifiée(s).
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <AlertTriangle className="h-4 w-4" />
            Divergence détectée à la séquence {verifyResult.firstDivergentSeq} — {verifyResult.reason}.
          </div>
        ))}

      {/* Log table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full min-w-[920px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-8 px-2 py-3"><span className="sr-only">Détails</span></th>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">MDU_ID</th>
                <th className="px-4 py-3">Flux</th>
                <th className="px-4 py-3">Mois</th>
                <th className="px-4 py-3">Contractant</th>
                <th className="px-4 py-3">Quadrant</th>
                <th className="px-4 py-3">Origine</th>
                <th className="px-4 py-3">Documenté par</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Empreinte</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm text-rose-600">
                    {error}
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-400">
                    Aucune entrée au journal pour ce filtre.
                  </td>
                </tr>
              ) : (
                entries.map((e) => {
                  const isOpen = expanded.has(e.seq);
                  return (
                    <Fragment key={e.seq}>
                      <tr className="hover:bg-slate-50/60">
                        <td className="px-2 py-2.5 text-center">
                          <button
                            type="button"
                            onClick={() => toggle(e.seq)}
                            aria-expanded={isOpen}
                            aria-label={isOpen ? 'Masquer les détails' : 'Afficher les détails'}
                            className="text-slate-400 transition-colors hover:text-slate-700"
                          >
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{e.seq}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-slate-700">{e.mduId}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className={
                              e.stream === 'SSV'
                                ? 'rounded-full bg-brand-purple/10 px-2 py-0.5 text-xs font-bold text-brand-purple'
                                : 'rounded-full bg-brand-rose/10 px-2 py-0.5 text-xs font-bold text-brand-rose'
                            }
                          >
                            {e.stream}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{e.invoicingMonth}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">{e.ssvContractor}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="purple">{e.designQuadrant}</Badge>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{e.rowOrigin}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{e.invoicingEligibilityCheckBy}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{frDate(e.invoicingEligibilityCheckDate)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-slate-400" title={e.totalHash}>
                          {e.totalHash.slice(0, 12)}…
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/40">
                          <td colSpan={11} className="px-6 py-4">
                            <LogDetail payload={e.payload} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
          <span className="font-bold text-slate-800">{entries.length}</span> entrée(s) affichée(s)
        </div>
      </div>
    </div>
  );
}
